//BasemapGallery.tsx ==============================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as MainMap from "../MainMap";
import * as MainToolbar from "../Menus/MainToolbar";
import * as ParcelSymbol from "../Util/ParcelSymbol";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as WidgetHeading from "../Widgets/WidgetHeading";
import * as Widgets from "../Widgets/Widgets";

import { Paper, Slider, Typography, Zoom } from "@mui/material";

import React from "react";

const toolName: string = "Basemap Gallery";
let toolDiv: string; //Container div of the widget.

let bmWidget: __esri.BasemapGallery; // Basemap gallery widget.
let basemapName: string = "";
let basemapNames: string[] = []; //Just a list of the basemap id's
let bmSliderValue: number = 0;
let opacityValue: number = 0;

//Initialize basemap gallery.
function initBasemapGallery() {
   if (basemapNames.length === 0) {
      basemapNames = [];

      for (const bm of MainMap.basemaps) {
         basemapNames.InsertAt(0, bm.id);
      }
   }
}

//Destroy the widget.
export function destroy() {
   if (bmWidget) {
      bmWidget = null;
   }
}

//Set slider values.
function setSliderValues() {
   if (basemapNames) {
      for (let i: number = 0; i < basemapNames.length; i++) {
         if (basemapNames[i] === MainMap.map.basemap.id) {
            bmSliderValue = i;

            const j: number = MainMap.basemaps.length - i - 1; //Get index of active basemap.
            opacityValue = MainMap.basemaps[j].baseLayers.getItemAt(0).opacity;
            break;
         }
      }
      basemapName = basemapNames[bmSliderValue];
   }
}

//Set basemap in gallery.
function setBasemap() {
   const basemap = getBaseMapOrBaseLayer(basemapName) as __esri.Basemap;

   if (basemap) {
      const baseLayer = getBaseMapOrBaseLayer(basemapName, true) as __esri.Layer;
      if (baseLayer) {
         opacityValue = baseLayer.opacity;
         baseLayer.opacity = opacityValue;
      }

      Widgets.setNextBasemap(basemap);
      Widgets.toggleBasemap(); //Used to force setting the basemap for different map projections.
   }
}

//Set basemap opacity.
function setOpacity() {
   const baseLayer = getBaseMapOrBaseLayer(basemapNames[bmSliderValue], true) as __esri.Layer;
   if (baseLayer) {
      baseLayer.opacity = opacityValue;
   }
}

//Get basemap or base layer.
function getBaseMapOrBaseLayer(name: string, baseLayer: boolean = false) {
   for (const basemap of MainMap.basemaps) {
      if (basemap.id === name) {
         if (baseLayer) {
            return basemap.baseLayers.getItemAt(0);
         } else {
            return basemap;
         }
      }
   }
   return null;
}

interface iProps {
   fadeIn: boolean;
}

//Render function.
export function Render(props: iProps) {
   Util.logMsg("Top of BasemapGallery Render");

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did update.
   React.useEffect(() => {
      //Load the ESRI api widget.
      import("@arcgis/core/widgets/BasemapGallery").then(({ default: BasemapGalleryWidget }) => {

         Util.logMsg("Basemap gallery did update");

         if (!bmWidget && props.fadeIn) {
            Util.logMsg("Creating Basemap Gallery widget");

            initBasemapGallery(); //Executes first time only in above code.
            setSliderValues();

            //Create widget.
            bmWidget = new BasemapGalleryWidget({
               container: toolDiv,
               view: MainMap.activeView,
               source: MainMap.basemaps,
            });

            refresh();

            //Basemap changed event.
            bmWidget.watch("activeBasemap", function (newValue: __esri.Basemap, oldValue: __esri.Basemap) {
               Util.logMsg("activeBasemap changed to: " + newValue.id);
               basemapName = newValue.id;
               ParcelSymbol.setParcelSymbol(basemapName);

               for (let i: number = 0; i < basemapNames.length; i++) {
                  if (basemapNames[i] === basemapName) {
                     bmSliderValue = i;

                     const baseLayer = getBaseMapOrBaseLayer(basemapNames[i], true) as __esri.Layer;
                     if (baseLayer) {
                        opacityValue = baseLayer.opacity;
                     }
                     break;
                  }
               }

               if (MainMap.map.basemap.id === basemapName) {
                  Widgets.setNextBasemap(oldValue); //Don't allow both toogle basemap basemaps to be the same.
               }

               MainToolbar.addOrRemove3D();
               MainToolbar.render();

               refresh();
            });

         }

      }).catch((error: Error) => {
         Util.errMsg('An error occurred while loading the Basemap Gallery module:', error);
      });
   });

   //Slider changed event.
   function sliderChanged(event: Event, value: number) {
      if (MainMap.activeView.updating) {
         return; //Quit if updating the view.
      }
      if (value !== bmSliderValue) {
         bmSliderValue = value;
         basemapName = basemapNames[value];
         setBasemap();
         refresh();
      }
   }

   //Opacity changed event.
   function opacityChanged(event: Event, value: number) {
      if (value !== opacityValue) {
         opacityValue = value;
         setOpacity();
         refresh();
      }
   }

   //Animation is complete.
   function fadeInCompleted() {
      bmWidget.visible = true;
   }
   function fadeOutCompleted() {
      if (bmWidget) {
         bmWidget.visible = false;
      }
   }

   //Close this tool.
   function hide() {
      DrawerMenu.closeTool();
   }

   //Styles.
   const styles: Default.iStyles = {
      appWidgetDiv: { position: 'absolute', top: 0, left: 0, zIndex: 3, minWidth: Settings.leftPanelWidth, overflow: 'auto' },
      slider: { width: 240 },
      gallery: { backgroundColor: Settings.paperColor, marginTop: 3 },
   };

   const scrollDivID: string = toolName.replace(" ", "_") + "_Scroll";

   toolDiv = "basemap-gallery-id-" + MainMap.view2D.toString();

   return (
      <Zoom in={props.fadeIn} timeout={500} onEntered={() => fadeInCompleted()} onExited={() => fadeOutCompleted()}>

         <div id="Basemap_Gallery_Widget" style={styles.appWidgetDiv}>
            <Paper>
               <WidgetHeading.Render title={toolName} onClose={hide} />

               <div id={scrollDivID} style={Default.styles.appToolDiv}>
                  <div className="app-flex-column-start" style={{ padding: 3 }}>

                     <Paper className="app-flex-column-start" style={{ padding: 6, paddingBottom: 3, paddingLeft: 20, paddingRight: 20, backgroundColor: Settings.leftPanelLabelColor }}>
                        <Typography id="basemap-label" variant="body2">{basemapName}</Typography>

                        <Slider style={styles.slider}
                           value={bmSliderValue}
                           aria-labelledby="basemap-label"
                           valueLabelDisplay="off"
                           marks
                           min={0}
                           max={MainMap.basemaps.length - 1}
                           step={1}
                           onChange={sliderChanged}
                        />

                        <Typography id="opacity-label" variant="body2">Opacity</Typography>

                        <Slider style={styles.slider}
                           value={opacityValue}
                           aria-labelledby="opacity-label"
                           valueLabelDisplay="off"
                           marks
                           min={0}
                           max={1}
                           step={0.05}
                           onChange={opacityChanged}
                        />
                     </Paper>

                     <div id={toolDiv} key={toolDiv} style={styles.gallery}>Basemap Gallery</div>
                  </div>
               </div>

            </Paper>
         </div>
      </Zoom>
   ); //end return.

} //end function.
